import React from 'react';

// Register GPU Server Modal
export function GpuRegisterModal({ show, servers, selectedServerId, setSelectedServerId, registering, registerError, onRegister, onClose }) {
  if (!show) return null;
  return (
    <div className="deploy-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="deploy-modal" style={{ maxWidth: 480 }}>
        <div className="deploy-modal-header">
          <h2>🖥 Register GPU Server</h2>
          <button className="deploy-close" onClick={onClose}>✕</button>
        </div>
        <div className="deploy-form">
          <div className="deploy-section">
            <div className="deploy-field">
              <label>Select Server</label>
              <select value={selectedServerId} onChange={e => setSelectedServerId(e.target.value)}
                className="select">
                <option value="">— Choose a server —</option>
                {servers.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.ip_address})</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 4 }}>
                The server must have NVIDIA drivers and nvidia-smi installed.
                An SSH private key must be configured on the server.
              </div>
            </div>
          </div>
          {registerError && <div className="deploy-error">{registerError}</div>}
          <div className="deploy-actions">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={onRegister} disabled={registering || !selectedServerId}>
              {registering ? 'Discovering GPUs...' : '🔍 Discover & Register'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Allocate GPU Instance Modal
export function GpuAllocateModal({ allocModal, allocImage, setAllocImage, allocHours, setAllocHours, allocating, onAllocate, onClose }) {
  if (!allocModal) return null;
  return (
    <div className="deploy-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="deploy-modal" style={{ maxWidth: 500 }}>
        <div className="deploy-modal-header">
          <h2>🚀 Allocate GPU Instance</h2>
          <button className="deploy-close" onClick={onClose}>✕</button>
        </div>
        <div className="deploy-form">
          <div className="deploy-section">
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{allocModal.instanceName}</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 2 }}>{allocModal.vramGb}GB VRAM</div>
            </div>
            <div className="deploy-field">
              <label>Docker Image</label>
              <select value={allocImage} onChange={e => setAllocImage(e.target.value)}
                className="select">
                <option value="pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime">PyTorch 2.1 + CUDA 12.1</option>
                <option value="tensorflow/tensorflow:2.14.0-gpu">TensorFlow 2.14 GPU</option>
                <option value="nvcr.io/nvidia/cuda:12.2.0-devel-ubuntu22.04">CUDA 12.2 Dev (Ubuntu 22.04)</option>
                <option value="nvcr.io/nvidia/pytorch:23.10-py3">NVIDIA PyTorch 23.10</option>
                <option value="jupyter/scipy-notebook:latest">Jupyter Scipy Notebook</option>
              </select>
            </div>
            <div className="deploy-field" style={{ marginTop: 12 }}>
              <label>Expires After</label>
              <select value={allocHours} onChange={e => setAllocHours(Number(e.target.value))}
                className="select">
                <option value={2}>2 hours</option>
                <option value={8}>8 hours</option>
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={168}>7 days</option>
              </select>
            </div>
          </div>
          <div className="deploy-actions">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={onAllocate} disabled={allocating}>
              {allocating ? '⟳ Provisioning...' : '🚀 Allocate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Allocation Result Modal
export function GpuAllocResultModal({ allocResult, onClose }) {
  if (!allocResult) return null;
  return (
    <div className="deploy-modal-overlay">
      <div className="deploy-modal" style={{ maxWidth: 520 }}>
        <div className="deploy-modal-header">
          <h2>{allocResult.error ? '⚠ Allocation Issue' : '✅ GPU Instance Ready'}</h2>
          <button className="deploy-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 24px 24px' }}>
          {allocResult.error ? (
            <div className="deploy-error">{allocResult.error}</div>
          ) : (
            <>
              {allocResult.warning && (
                <div className="banner warning" style={{ marginBottom: 16 }}>
                  ⚠ {allocResult.warning}
                </div>
              )}
              <div className="gpu-connect-box" style={{ marginBottom: 16 }}>
                <div className="gpu-connect-row">
                  <span>SSH Command</span>
                  <code>ssh root@{allocResult.ssh_host} -p {allocResult.ssh_port}</code>
                </div>
                <div className="gpu-connect-row">
                  <span>Password</span>
                  <code>{allocResult.ssh_password}</code>
                </div>
                <div className="gpu-connect-row">
                  <span>Jupyter</span>
                  <a href={allocResult.jupyter_url} target="_blank" rel="noreferrer" className="gpu-jupyter-link">
                    {allocResult.jupyter_url}
                  </a>
                </div>
                <div className="gpu-connect-row">
                  <span>GPU</span>
                  <span>{allocResult.gpu_model} · {allocResult.vram_gb}GB VRAM</span>
                </div>
                <div className="gpu-connect-row">
                  <span>Expires</span>
                  <span>{allocResult.expires_at ? new Date(allocResult.expires_at).toLocaleString() : '—'}</span>
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted-2)' }}>
                Save your SSH password — it won't be shown again. The container may take ~30s to fully start.
              </div>
            </>
          )}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={onClose}>
              View My Instances →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Web Terminal Modal
export function GpuTerminalModal({ terminalAlloc, termHistory, termInput, setTermInput, termRunning, onExec, onClose }) {
  if (!terminalAlloc) return null;
  return (
    <div className="deploy-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="deploy-modal" style={{ maxWidth: 760, width: '95vw' }}>
        <div className="deploy-modal-header" style={{ background: '#1a202c', borderRadius: '10px 10px 0 0', padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#68d391', fontSize: 13 }}>⬛</span>
            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>
              Terminal — {terminalAlloc.instance_name} ({terminalAlloc.gpu_model})
            </span>
            <span style={{ color: '#718096', fontSize: 12 }}>
              container: {terminalAlloc.container_name}
            </span>
          </div>
          <button className="deploy-close" onClick={onClose} style={{ color: '#a0aec0' }}>✕</button>
        </div>
        {/* Terminal output */}
        <div style={{ background: '#0d1117', minHeight: 360, maxHeight: 460, overflowY: 'auto', padding: 16, fontFamily: 'monospace', fontSize: 13 }}
          ref={el => el && (el.scrollTop = el.scrollHeight)}>
          <div style={{ color: '#68d391', marginBottom: 8, fontSize: 12 }}>
            Connected to GPU container via docker exec. Type commands below.
          </div>
          {termHistory.map((h, i) => (
            <div key={i} style={{ marginBottom: 2 }}>
              <span style={{ color: h.type === 'input' ? '#faf089' : h.type === 'error' ? '#fc8181' : '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {h.text}
              </span>
            </div>
          ))}
          {termRunning && <div style={{ color: '#718096', animation: 'pulse 1s infinite' }}>Running...</div>}
        </div>
        {/* Input */}
        <form onSubmit={onExec} style={{ display: 'flex', background: '#1a202c', padding: '8px 12px', gap: 8, borderRadius: '0 0 10px 10px' }}>
          <span style={{ color: '#68d391', fontFamily: 'monospace', fontSize: 14, alignSelf: 'center' }}>$</span>
          <input
            autoFocus
            value={termInput}
            onChange={e => setTermInput(e.target.value)}
            disabled={termRunning}
            placeholder="enter command..."
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#e2e8f0', fontFamily: 'monospace', fontSize: 13 }}
          />
          <button type="submit" disabled={termRunning || !termInput.trim()}
            style={{ background: '#2d3748', color: '#68d391', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>
            Run
          </button>
        </form>
      </div>
    </div>
  );
}
